import React, { useState, useEffect } from 'react';
import {CheckCircleIcon, ExclamationCircleIcon, KeyIcon, RocketLaunchIcon} from '@heroicons/react/24/outline';
import { useOnboarding } from '../../contexts/OnboardingProvider';
import { useNavigate } from 'react-router-dom';

export const OnboardingChecklist = () => {
    const { startTutorial } = useOnboarding();
    const navigate = useNavigate();
    const [checklistData, setChecklistData] = useState({
        openaiConfigured: false,
        geminiConfigured: false,
        platformsConfigured: false,
        contentSourcesConfigured: false
    });

    const mobileOptimizedClasses = {
        container: "bg-white shadow rounded-lg p-4 sm:p-6",
        header: "flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-1 sm:space-y-0",
        progressSection: "mb-4 sm:mb-6",
        checklistItem: "flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border border-gray-200 rounded-lg space-y-2 sm:space-y-0",
        buttonAction: "text-sm text-blue-600 hover:text-blue-500 font-medium px-3 py-1 self-start sm:self-auto touch-manipulation min-h-[36px] flex items-center justify-center"
    };

    useEffect(() => {
        checkSetupStatus();
    }, []);

    const checkSetupStatus = async () => {
        try {
            const response = await fetch('/api/user/setup-status', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setChecklistData(data.data);
            }
        } catch (error) {
            console.error('Failed to check setup status:', error);
        }
    };

    // In OnboardingChecklist.jsx, update the checklistItems:
    const checklistItems = [
        {
            id: 'gemini',
            title: 'Gemini API Key',
            description: 'Primary AI provider for content generation',
            completed: checklistData.geminiConfigured,
            required: true,
            action: () => window.location.hash = '#credentials'
        },
        {
            id: 'openai',
            title: 'OpenAI API Key',
            description: 'Optional: Alternative AI capabilities',
            completed: checklistData.openaiConfigured,
            required: false,
            action: () => window.location.hash = '#credentials'
        },
        {
            id: 'platforms',
            title: 'Social Platforms',
            description: 'Connect LinkedIn, Twitter, etc.',
            completed: checklistData.platformsConfigured,
            required: false,
            action: () => window.location.hash = '#platforms'  // Navigate to platforms tab
        },
        {
            id: 'content',
            title: 'Content Sources',
            description: 'Add websites for inspiration',
            completed: checklistData.contentSourcesConfigured,
            required: false,
            action: () => window.location.hash = '#content-sources'  // Navigate to content sources tab
        }
    ];

    const completedCount = checklistItems.filter(item => item.completed).length;
    const progressPercentage = (completedCount / checklistItems.length) * 100;

    return (
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-1 sm:space-y-0">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Setup Checklist</h3>
                <span className="text-xs sm:text-sm text-gray-500">{completedCount}/{checklistItems.length} complete</span>
            </div>

            {/* Progress bar */}
            <div className="mb-4 sm:mb-6">
                <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-2">
                    <span>Setup Progress</span>
                    <span>{Math.round(progressPercentage)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 sm:h-2">
                    <div
                        className="bg-blue-600 h-2 sm:h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>
            </div>

            {/* Checklist items */}
            <div className="space-y-2 sm:space-y-3">
                {checklistItems.map((item) => (
                    <div key={item.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-3 border border-gray-200 rounded-lg space-y-2 sm:space-y-0">
                        <div className="flex items-start sm:items-center space-x-3 flex-1">
                            <div className="flex-shrink-0 mt-0.5 sm:mt-0">
                                {item.completed ? (
                                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                ) : item.required ? (
                                    <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />
                                ) : (
                                    <div className="h-5 w-5 border-2 border-gray-300 rounded-full" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0">
                                    <h4 className="text-sm sm:text-sm font-medium text-gray-900 truncate">{item.title}</h4>
                                    {item.required && !item.completed && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 self-start sm:self-auto">
                                Required
                            </span>
                                    )}
                                </div>
                                <p className="text-xs sm:text-sm text-gray-500 mt-1">{item.description}</p>
                            </div>
                        </div>
                        {!item.completed && (
                            <button
                                onClick={item.action}
                                className="text-sm text-blue-600 hover:text-blue-500 font-medium px-3 py-1 self-start sm:self-auto touch-manipulation min-h-[36px] flex items-center justify-center"
                            >
                                Set up
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {completedCount < checklistItems.length && (
                <div className="mt-4 sm:mt-6 pt-4 border-t border-gray-200">
                    <button
                        onClick={() => {
                            startTutorial(); // Set the state
                            navigate('/dashboard'); // Navigate to dashboard
                        }}
                        className="w-full inline-flex justify-center items-center px-4 py-3 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 touch-manipulation min-h-[44px]"
                    >
                        <RocketLaunchIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="truncate">Complete Setup Tutorial</span>
                    </button>
                </div>
            )}
        </div>
    );
};